import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { VoiceRecognitionUtilityService } from './voice-recognition-utility.service';
import { VoiceRecognitionService } from './voice-recognition.service';
import { logger } from '../../services/logging/logging.service';
import { ResponsiveDesignService } from '../../services/responsive-design/responsive-design.service';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';
import { ExternalSettingsService } from '../../services/external-settings/external-settings.service';
import { ExternalSettings } from '../../services/external-settings/external-settings.interface';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { WaveformService } from '../speech-to-text/waveform.service';
import { AnimationItem } from 'lottie-web';
import { successBlueAnimation } from '../../lottie/lottie-animations';

@Component({
    selector: 'app-voice-recognition',
    templateUrl: './voice-recognition.component.html',
    styleUrls: ['./voice-recognition.component.less'],
    standalone: false
})
export class VoiceRecognitionComponent implements OnInit, OnDestroy {

  private VISUALIZER_BUFFER_LENGTH = 180;
  private MAX_RECORDING_TIME = 10 * 1000; // Default 10 seconds in milliseconds
  protected visualizerData: number[] = [];
  protected isRecording = false;
  protected isClosing: boolean = false;
  private recordingTimeout: any;

  private destroy$: Subject<void> = new Subject();

  protected message: string = '';
  protected UIUtilityService: any = UIUtilityService;
  protected successBlueAnimation = successBlueAnimation;
  protected externalSettings!: ExternalSettings;
  private srvRecongnition: VoiceRecognitionService;

  constructor(
    protected srvRpDesign: ResponsiveDesignService,
    private modalCtrl: ModalController,
    private srvChangeDetector: ChangeDetectorRef,
    private srvES: ExternalSettingsService,
    private srvRecognitionUtility: VoiceRecognitionUtilityService,
    private audio: WaveformService,
    private ngZone: NgZone,
  ) {

    this.srvRecongnition = this.srvRecognitionUtility.getVoiceRecognitionService();
    this.srvRecongnition.init();
    this.audio.init();

    this.srvRecongnition.message().subscribe((message: string) => {
      logger.debug("message was fired", message);
      this.message = message;
      // Use markForCheck instead of detectChanges to avoid ExpressionChanged error
      this.srvChangeDetector.markForCheck();
    });

    this.srvRecongnition.isCompleted().subscribe((isComplete) => {
      if (isComplete) {
        this.closeDialog();
      };
    });
  }

  ngOnInit() {
    combineLatest([
      this.srvES.externalSettings$,
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([externalSettings]) => {
        this.externalSettings = externalSettings;
        this.MAX_RECORDING_TIME = this.externalSettings.globalVariables.speechToTextMaxMilliSec;
        this.VISUALIZER_BUFFER_LENGTH = this.srvRpDesign.isMobileView() ? 70 : 180;
        this.toggleRecording();
      });
  }

  protected async close() {
    let isListening = await this.srvRecongnition.isListening();
    if (isListening) {
      this.srvRecongnition.stop();
    }
    this.closeDialog();
  }

  protected toggleRecording() {
    this.isRecording = !this.isRecording;

    if (this.isRecording) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  private startRecording() {
    this.srvRecongnition.start();
    this.audio.startRecording();

    this.resetWaveform();
    this.drawWaveform();

    this.recordingTimeout = setTimeout(() => {
      this.stopRecording();
    }, this.MAX_RECORDING_TIME);
  }

  private stopRecording() {
    this.isRecording = false;
    clearTimeout(this.recordingTimeout);
    this.audio.stopRecording();
    this.srvRecongnition.stop();
  }


  private closeDialog() {
    this.isClosing = true;
    setTimeout(() => {
      this.modalCtrl.dismiss(this.message, 'message');
    }, 1000);
  }

  protected onAnimationCreated(animationItem: AnimationItem): void {
    animationItem.setSpeed(3);
  }

  // Initialize the visualizerData array with zeros
  private resetWaveform() {
    this.visualizerData = Array(this.VISUALIZER_BUFFER_LENGTH).fill(0);
  }

  ngOnDestroy() {
    this.isRecording = false;
    this.destroy$.next();
    this.destroy$.complete();
  }

  private drawWaveform = () => {
    this.ngZone.runOutsideAngular(() => {
      const draw = () => {
        if (!this.isRecording || this.isClosing) return;
        
        const point = this.audio.getAudioData();
        
        this.visualizerData.unshift(point);
        if (this.visualizerData.length > this.VISUALIZER_BUFFER_LENGTH) {
          this.visualizerData.pop();
        }

        // Trigger change detection manually since we're running outside Angular zone
        // This ensures the waveform updates at 60fps without overhead to the whole app
        if (!(this.srvChangeDetector as any).destroyed) {
          this.srvChangeDetector.detectChanges();
        }
        
        requestAnimationFrame(draw);
      };
      
      // Use requestAnimationFrame to start the loop to ensure we're out of the init cycle
      requestAnimationFrame(draw);
    });
  };

  protected trackByIndex(index: number): number {
    return index;
  }

  protected getWaveform(rms: number) {
    return {
      'height': `${Math.min(100, Math.max(14, rms * 100))}%`,
    }
  }

}
